import React, { useContext, useState } from 'react'
import styled from 'styled-components'
import i18n from 'live/services/i18n'
import dateService from 'live/services/date'
import globalNotification from 'live/services/global-notification'
import globalConfirmation from 'live/services/global-confirmation'
import { deleteAsset, deleteFromPluginUploads, getAssetTree } from '../common/apis'
import { FACTORY_CONFIG_ID } from '../common/utils'
import { configsContext } from './AssetConfigs'
import { FactoryDialog } from './factoryDialog'
import { ProductDialog } from './productDialog'
interface IProps {
    assetName: string
    secondaryInfo: string | JSX.Element
    date: number
    author: string
    isSelected: boolean
    setIsSelected: (arg: boolean) => void
    rowType: 'product' | 'factory' | 'line' | 'sku'
    productName?: string
    factoryName?: string
    countryName?: string
    numOfConfiguredProducts?: number
    skuColor?: string
    productImageId?: string
}
export function TableRow({
    assetName,
    secondaryInfo,
    date,
    author,
    isSelected,
    setIsSelected,
    rowType,
    productName,
    productImageId,
    factoryName,
    countryName,
    numOfConfiguredProducts,
    skuColor
}: IProps): JSX.Element {
    const [showDialog, setShowDialog] = useState(false)
    const { assetType, setAssetTree } = useContext(configsContext)
    const [parentProduct, setParentProduct] = useState('')
    const [isDialogOnEditMode, setIsDialogOnEditMode] = useState(false)
    const childTypes = {
        product: 'sku',
        factory: 'line'
    }
    function getLineAddOrEditLink(type: 'add' | 'edit'): string {
        const urlBase = `.#/config/${FACTORY_CONFIG_ID}`
        if (type === 'edit') {
            return urlBase + `/editLine?country=${countryName}&factory=${factoryName}&line=${assetName}`
        } else {
            return urlBase + `/newLine?country=${countryName}&factory=${assetName}`
        }
    }
    function onClickAddOrEdit(type: 'add' | 'edit', event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void {
        event.stopPropagation()
        if (rowType === 'factory' && type === 'add') {
            window.location.href = getLineAddOrEditLink('add')
        } else if (rowType === 'line' && type === 'edit') {
            window.location.href = getLineAddOrEditLink('edit')
        } else if (rowType === 'product' && type === 'add') {
            setIsDialogOnEditMode(false)
            setShowDialog(true)
            setParentProduct(assetName)
        } else {
            setParentProduct(productName)
            setIsDialogOnEditMode(true)
            setShowDialog(true)
        }
    }

    return (
        <React.Fragment>
            <StyledTableRow
                className={`TableRow ${childTypes[rowType] ? '' : 'childRow'} ${
                    isSelected && (rowType === 'factory' || rowType === 'product') ? 'isSelected' : ''
                }`}
                onClick={() => {
                    if (childTypes[rowType]) setIsSelected(!isSelected)
                }}
            >
                <td className="TableRow--asset">
                    <i className={`fas fa-chevron-${isSelected ? 'down' : 'right'}`} />
                    {assetName}
                </td>
                <td
                    className={`TableRow--secondaryInfo ${
                        rowType === 'line' && !numOfConfiguredProducts ? 'emptyLine' : ''
                    }`}
                >
                    {secondaryInfo}
                </td>
                <td className="TableRow--date">{dateService.formatDateTime(date)}</td>
                <td className="TableRow--author">{author}</td>
                <td className="TableRow--addChild">
                    {rowType === 'sku' ? (
                        <div className="TableRow--skuColor" style={{ backgroundColor: skuColor || '#ffffff' }}></div>
                    ) : null}

                    <button className="TableRow--addChild__button" onClick={event => onClickAddOrEdit('add', event)}>
                        <i className="far fa-plus-circle" />
                        {`${i18n('add')} ${i18n(childTypes[rowType])}`}
                    </button>
                </td>

                <td className="TableRow--editAsset">
                    <button
                        className="TableRow--editAsset__button"
                        aria-label={i18n('edit')}
                        onClick={event => onClickAddOrEdit('edit', event)}
                    >
                        <i className="far fa-edit" />
                    </button>
                </td>
                <td className="TableRow--removeAsset">
                    <button
                        aria-label={`${i18n('delete')} ${i18n(rowType)}`}
                        className="TableRow--removeAsset__button"
                        onClick={event =>
                            onRemoveAsset(
                                event,
                                assetName,
                                assetType,
                                setAssetTree,
                                {
                                    country: countryName,
                                    factory: rowType === 'factory' ? assetName : factoryName,
                                    line: rowType === 'line' ? assetName : undefined,
                                    productName: rowType === 'product' ? assetName : productName,
                                    skuName: rowType === 'sku' ? assetName : undefined
                                },
                                productImageId
                            )
                        }
                    >
                        <i className="far fa-trash-alt" />
                    </button>
                </td>
            </StyledTableRow>
            {showDialog && assetType === 'product' ? (
                <ProductDialog
                    showModal={showDialog}
                    setShowModal={setShowDialog}
                    isEdit={isDialogOnEditMode}
                    currentId={secondaryInfo as string}
                    currentName={assetName}
                    currentProductImageId={productImageId}
                    parentProduct={parentProduct}
                />
            ) : null}
            {showDialog && assetType === 'factory' ? (
                <FactoryDialog
                    showModal={showDialog}
                    setShowModal={setShowDialog}
                    isEdit={isDialogOnEditMode}
                    currentCountr={secondaryInfo as string}
                    currentName={assetName}
                />
            ) : null}
        </React.Fragment>
    )
}

export function onRemoveAsset(
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    assetName: string,
    assetType: AssetType,
    setAssetTree: (assetTree: CountryData[] | ProductData[]) => void,
    params: {
        country?: string
        factory?: string
        line?: string
        productName?: string
        skuName?: string
    },
    productImageId?: string
): void {
    event.stopPropagation()
    globalConfirmation.danger(
        <span>{`${i18n('Are you sure you want to delete')} ${assetName} ?`}</span>,
        null,
        () => {
            deleteAsset(params)
                .then(() => {
                    getAssetTree(assetType).then(response => {
                        setAssetTree(response)
                        globalNotification.success(i18n('Deleted'))
                    })
                })
                .then(() => deleteFromPluginUploads(productImageId))
        },
        {
            btnText: i18n('DELETE')
        }
    )
}

const StyledTableRow = styled.tr`
    border-top: 1px solid ${({ theme }) => theme['gray-400']};
    &.childRow {
        background: ${({ theme }) => theme['gray-100']};
    }
    &:hover {
        background: ${({ theme }) => theme['blue-200']};
        cursor: pointer;
    }
    &:hover,
    &.isSelected {
        > td button {
            color: var(--blue-500);
        }
        .TableRow--removeAsset__button {
            color: var(--red-500);
        }
    }

    .TableRow {
        &--asset,
        &--line,
        &--products,
        &--secondaryInfo,
        &--author,
        &--date,
        &--addChild,
        &--editAsset,
        &--editLine,
        &--removeAsset {
            text-align: center;
            padding: 15px 10px;
        }
        &--asset,
        &--line {
            font-family: ${({ theme }) => theme['font-semibold']};
            text-align: start;

            i {
                margin-right: 10px;
                color: var(--blue-500);
            }
        }
        &--line {
            padding-left: 50px;
        }
        &--addChild,
        &--editAsset,
        &--editLine,
        &--removeAsset {
            &__button {
                transition: all 0.3s;
                border: none;
                background: none;
                font-size: 13px;
                line-height: 13px;
                padding: 15px 10px;
            }
        }
        &--addChild,
        &--editAsset,
        &--editLine {
            &__button {
                text-decoration: none;
                color: transparent;
                text-transform: capitalize;
                font-family: ${({ theme }) => theme['font-semibold']};
                i {
                    margin-right: 8px;
                }
            }
        }
        &--addChild {
            white-space: nowrap;
        }
        &--removeAsset {
            text-align: start;
            &__button {
                color: transparent;
            }
        }
        &--secondaryInfo.emptyLine {
            color: var(--red-700);
        }
        &--skuColor {
            display: none;
        }
    }
    &.childRow {
        .TableRow--asset {
            padding-left: 50px;
            i {
                display: none;
            }
        }
        .TableRow--addChild {
            &__button {
                display: none;
            }
        }

        .TableRow--skuColor {
            display: flex;
            width: 20px;
            height: 20px;
        }
    }
`
